import { useMediaQuery, useTheme } from '@mui/material'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { UnitTypes } from 'src/Libs/types'
import { DynamicPriceType } from '../types'
import { getReferenceLineYPosition } from '../utils/power-price-widget'

type UseReferenceLineConfigProps = {
  wallboxLimit: number
  batteryLimit: number
  minPrice: number
  maxPrice: number
  selectedDynamicPowerPriceModalSetting: DynamicPriceType
  isModal?: boolean
  isDynamicPowerPriceEnabledForBattery: boolean
  isDynamicPowerPriceEnabledForWallboxes: boolean
}

type UseReferenceLineConfig = {
  showReferenceLineForWallbox: boolean
  showReferenceLineForBattery: boolean
  wallboxReferenceLineYPosition: number
  batteryReferenceLineYPosition: number
  formattedWallboxLimit: string
  formattedBatteryLimit: string
  dynamicPriceColorConfig: Record<DynamicPriceType, string>
}

/*
 * a hook that returns configuration values for the reference lines for the dpp widget
 */
const useReferenceLineConfig = ({
  wallboxLimit,
  batteryLimit,
  selectedDynamicPowerPriceModalSetting,
  minPrice,
  maxPrice,
  isModal,
  isDynamicPowerPriceEnabledForBattery,
  isDynamicPowerPriceEnabledForWallboxes,
}: UseReferenceLineConfigProps): UseReferenceLineConfig => {
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const isBothDynamicPowerPriceEnabled = isDynamicPowerPriceEnabledForBattery && isDynamicPowerPriceEnabledForWallboxes

  const wallboxReferenceLineYPosition = getReferenceLineYPosition(wallboxLimit, minPrice, maxPrice, downMd, isModal)
  const batteryReferenceLineYPosition = getReferenceLineYPosition(batteryLimit, minPrice, maxPrice, downMd, isModal)

  const showReferenceLineForWallbox =
    (isModal && isBothDynamicPowerPriceEnabled && selectedDynamicPowerPriceModalSetting === DynamicPriceType.Wallbox) ||
    (isModal && !isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForWallboxes) ||
    (!isModal && isDynamicPowerPriceEnabledForWallboxes)
  const showReferenceLineForBattery =
    (isModal && isBothDynamicPowerPriceEnabled && selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery) ||
    (isModal && !isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForBattery) ||
    (!isModal && isDynamicPowerPriceEnabledForBattery)

  const formattedWallboxLimit = `${getLocalizedNumber(wallboxLimit)} ${UnitTypes.centsPerKiloWattHour}`
  const formattedBatteryLimit = `${getLocalizedNumber(batteryLimit)} ${UnitTypes.centsPerKiloWattHour}`

  const dynamicPriceColorConfig = {
    [DynamicPriceType.Wallbox]: theme.palette.dppWidget.wallboxPriceLimitColor,
    [DynamicPriceType.Battery]: theme.palette.dppWidget.batteryPriceLimitColor,
  }

  return {
    showReferenceLineForWallbox,
    showReferenceLineForBattery,
    wallboxReferenceLineYPosition,
    batteryReferenceLineYPosition,
    formattedWallboxLimit,
    formattedBatteryLimit,
    dynamicPriceColorConfig,
  }
}

export default useReferenceLineConfig
